import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { fireSubmitSuggestionFeedbackUnifiedAnalytics } from '../ui/utils/analytics';

import type { OnSubmitSuggestionFeedback, SubmitSuggestionFeedbackAction } from './types';

/**
 * Submit user feedback (good / bad) for a suggestion.
 *
 * Fires the canonical unified `aiFeedback submitted` event so the vote is
 * captured by the AI thumbs-feedback pipeline. Returns `false` (and does
 * nothing) when the target suggestion no longer exists.
 *
 * The suggestion `id` is used as the `singleInstrumentationID` for now.
 *
 * When the product has supplied an `onSubmitSuggestionFeedback` callback, the
 * feedback is also forwarded to that product-owned destination (e.g. a
 * feedback-collector service project).
 *
 * TODO: EDITOR-8269 - Pipe the backend-assigned instrumentation id through the
 * skill and use it for `singleInstrumentationID` instead of the suggestion id.
 */
export const submitSuggestionFeedbackAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	onSubmitSuggestionFeedback?: OnSubmitSuggestionFeedback,
) => SubmitSuggestionFeedbackAction =
	(
		api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
		onSubmitSuggestionFeedback?: OnSubmitSuggestionFeedback,
	) =>
	(id, sentiment, options): boolean => {
		const pluginState = api?.aiSuggestions?.sharedState.currentState();
		const suggestion = pluginState?.suggestionById.get(id);
		if (!suggestion) {
			return false;
		}
		const interactionPoint = options?.interactionPoint ?? 'card';
		fireSubmitSuggestionFeedbackUnifiedAnalytics(
			api?.analytics?.actions,
			sentiment,
			id,
			interactionPoint,
		);
		// Forward the feedback to the product-owned destination, when wired.
		onSubmitSuggestionFeedback?.({
			interactionPoint,
			sentiment,
			suggestionId: id,
			suggestionType: suggestion.type,
		});
		return true;
	};
